import { Alert, Checkbox, Stack } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { apiWithoutCamelization } from "../../../../../api";
import { mieterstromInvoicingApiWithoutCamelization } from "../../../../../api/subApis/mieterstromInvoicingApi";
import { getDefaultMRTOptions } from "../../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { AlertPresets } from "../../../../../mantine/mantinePresets";
import { useToast } from "../../../../../mantine/notifications/useToast";
import type {
  Delivery,
  Generator,
  Person,
  Site
} from "../../../../../utils/backend-types";
import { DeliveryType } from "../../../../../utils/backend-types";
import { MultiConfirmationModal } from "../../../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { useSupplierGenerators } from "../../../../DeliveryInvoices/hooks/useSupplierGenerators";
import type { Contract } from "../../../DeliveryOverview.types";
import { useDeliveryMutations } from "../../DeliveryModal/hooks/useDeliveryMutations";
import { getCurrentFormula } from "../utils/getCurrentFormula";
import { getCurrentPrice } from "../utils/getCurrentPrice";

export interface ContractDeliverySubComponentProps {
  contract: Contract;
  deliveries: Array<Delivery>;
  sites: Array<Site>;
  persons: Array<Person>;
  projectId: string;
  onDeliveriesUpdated: () => void;
}

const SITE_NOT_FOUND_TEXT = "Liegenschaft nicht gefunden";

const DELIVERY_KIND_VALUE_TO_LABEL = {
  [DeliveryType.DirectDelivery]: "Direktlieferung",
  [DeliveryType.ForwardingDelivery]: "Weiterleitung"
};

function ContractDeliverySubComponent({
  contract: initialContract,
  deliveries: initialDeliveries,
  sites,
  persons,
  projectId,
  onDeliveriesUpdated
}: ContractDeliverySubComponentProps) {
  const { showToast, showErrorToast } = useToast();
  const [contract, setContract] = useState(initialContract);
  const [deliveries, setDeliveries] = useState(initialDeliveries);
  const endpointUrl = mieterstromInvoicingApiWithoutCamelization.contract.url(
    contract.id
  );
  const hasMissingDeliveries = !deliveries || deliveries.length === 0;
  const [deliveriesForDeletion, setDeliveriesForDeletion] = useState<
    Array<Delivery>
  >([]);

  const navigate = useNavigate();

  const { deliveryDeletionMutation } = useDeliveryMutations(projectId);

  useEffect(() => {
    setDeliveries(initialDeliveries);
  }, [initialDeliveries]);

  function handleDeliveriesDeleted(ids: Array<number | string>) {
    const newDeliveries = deliveries.filter(
      (delivery) => !ids.includes(delivery.id)
    );
    setDeliveries(newDeliveries);
    onDeliveriesUpdated();
  }

  async function handleUpdateAutomaticSendInvoice() {
    const newContract = { ...contract };
    newContract.automatic_send_invoice_active =
      !contract.automatic_send_invoice_active;
    if (await submitContract(newContract)) {
      setContract(newContract);
    }
  }

  async function handleUpdateAllowance() {
    const newContract = { ...contract };
    newContract.allowance_send_invoice_via_mail =
      !contract.allowance_send_invoice_via_mail;
    if (await submitContract(newContract)) {
      setContract(newContract);
    }
  }

  async function submitContract(data: Contract): Promise<boolean> {
    if (contract) {
      try {
        await apiWithoutCamelization.put(endpointUrl, data);
      } catch (e) {
        if (e.response.data.automatic_send_invoice_active) {
          e.response.data.automatic_send_invoice_active.forEach((element) => {
            showErrorToast(element);
          });
        }
        return false;
      }
    }

    return true;
  }

  function handleDeliveriesDeletion() {
    const allPromises = deliveriesForDeletion.map((delivery) =>
      deliveryDeletionMutation.mutateAsync(delivery.id)
    );
    return Promise.all(allPromises)
      .then(() => {
        showToast({
          type: "success",
          message: "Die Lieferungen wurden erfolgreich gelöscht"
        });
        handleDeliveriesDeleted(
          deliveriesForDeletion.map((delivery) => delivery.id)
        );
        onDeliveriesUpdated();
      })
      .catch((error) => {
        showErrorToast(`Fehler beim Löschen der Lieferungen: ${error.message}`);
        return Promise.reject(error);
      })
      .finally(() => {
        setDeliveriesForDeletion([]);
      });
  }

  function getDeliveryNamesForDeletion() {
    return deliveriesForDeletion.map((delivery) => {
      const site = sites.find((site) => site.id === delivery.site);
      const siteName = site ? site.name : SITE_NOT_FOUND_TEXT;
      const supplierId = contract ? contract.supplier : null;
      const supplier = persons.find((person) => person.id === supplierId);
      const supplierName = supplier ? supplier.name : "Unbekannt";
      const suppliedId = contract ? contract.supplied : null;
      const supplied = persons.find((person) => person.id === suppliedId);
      const suppliedName = supplied ? supplied.name : "Unbekannt";

      return `${DELIVERY_KIND_VALUE_TO_LABEL[delivery.deliveryKind]} ${siteName}: ${supplierName} → ${suppliedName}`;
    });
  }

  const cols: Array<MRT_ColumnDef<Delivery>> = [
    {
      accessorKey: "deliveryKind",
      header: "Art der Lieferung",
      Cell: ({ row }) => DELIVERY_KIND_VALUE_TO_LABEL[row.original.deliveryKind]
    },
    {
      accessorKey: "generator",
      header: "Erzeuger",
      Cell: ({ row }) =>
        row.original.generators[0] && row.original.site ? (
          <GeneratorCell
            contractId={contract.id}
            deliveryGenerators={row.original.generators}
            deliveryId={row.original.id}
            siteId={row.original.site}
          />
        ) : null
    },
    {
      accessorKey: "site",
      header: "Liegenschaft",
      Cell: ({ row }) => <SiteCell siteId={row.original.site} sites={sites} />
    },
    {
      accessorKey: "workingPrices",
      header: "Preis (exkl. MWSt.)",
      Cell: ({ row }) =>
        row.original.useSpotMarketPrices
          ? getCurrentFormula(row.original.spotMarketPrices)
          : getCurrentPrice(row.original.workingPrices) + " ct/kWh"
    }
  ];

  const table = useMantineReactTable<Delivery>({
    ...getDefaultMRTOptions({
      hideBorder: true
    }),
    enableToolbarInternalActions: false,
    ...getMRTTopToolbarCustomActionsRenderer({
      create: {
        text: "Lieferung hinzufügen",
        variant: "outline",
        onClick: () => navigate(`./neue-lieferung?contract=${contract.id}`, {})
      },
      delete: {
        onClick: (rowSelection) => {
          setDeliveriesForDeletion(
            Object.keys(rowSelection).map(
              (id) =>
                deliveries.find((delivery) => delivery.id === id) as Delivery
            )
          );
        }
      }
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        edit: {
          text: "Lieferung bearbeiten",
          onClick: (row) => {
            table.setEditingRow(null);
            navigate(`./lieferung/${row.original.id}`);
          }
        },
        delete: {
          text: "Lieferung löschen",
          onClick: (row) => {
            setDeliveriesForDeletion([row.original]);
          }
        }
      }
    }),
    enableSorting: false,
    enablePagination: false,
    mantinePaperProps: { bd: "none", bg: "transparent", shadow: "none" },
    mantineTopToolbarProps: { bg: "transparent" },
    mantineBottomToolbarProps: { bg: "transparent" },
    columns: cols,
    data: deliveries
  });

  return (
    <Stack className="ContractDeliverySubComponent" gap="sm" mx="lg" my="lg">
      {hasMissingDeliveries && (
        <Alert
          color={AlertPresets.warning.color}
          icon={AlertPresets.warning.icon}
          mb="md"
        >
          Dieser Vertrag enthält keine Stromlieferungen. Bitte fügen Sie diese
          hinzu, indem Sie auf den Button &quot;Lieferung hinzufügen&quot;
          klicken.
        </Alert>
      )}
      <Checkbox
        checked={contract.allowance_send_invoice_via_mail}
        label="Zustimmung des Belieferten zum Rechnungsversand per E-Mail vorhanden"
        onChange={() => handleUpdateAllowance()}
      />
      <Checkbox
        checked={contract.automatic_send_invoice_active}
        disabled={!contract.allowance_send_invoice_via_mail}
        label="Automatischen Rechnungsversand aktivieren"
        onChange={() => handleUpdateAutomaticSendInvoice()}
      />
      <MantineReactTable table={table} />
      <MultiConfirmationModal
        actionName="löschen"
        actionObjects={getDeliveryNamesForDeletion()}
        confirmationText="Möchten Sie die folgenden Lieferungen löschen? Dieser Schritt kann nicht rückgängig gemacht werden."
        headerText="Lieferungen löschen?"
        isModalOpen={deliveriesForDeletion.length > 0}
        objectName="Lieferung"
        toggleModal={() => {
          setDeliveriesForDeletion([]);
          table.setRowSelection({});
        }}
        onAction={handleDeliveriesDeletion}
      />
    </Stack>
  );
}

interface SiteCellProps {
  siteId: number | null;
  sites: Array<Site>;
}

function SiteCell({ siteId, sites }: SiteCellProps) {
  const site = sites.find((site) => site.id === siteId);
  const displayName = site ? site.name : SITE_NOT_FOUND_TEXT;

  return <span>{displayName}</span>;
}

interface GeneratorCellProps {
  siteId: number;
  deliveryId: string;
  deliveryGenerators: Array<number>;
  contractId: string;
}

function GeneratorCell({
  siteId,
  deliveryId,
  deliveryGenerators,
  contractId
}: GeneratorCellProps) {
  const { data: generators = [] } = useSupplierGenerators(contractId, siteId, {
    enabled: contractId !== undefined && siteId !== undefined,
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false
  });
  const supplierGeneratorsForDelivery = generators.filter((generator) =>
    deliveryGenerators.includes(generator.id)
  );

  return (
    <>
      {supplierGeneratorsForDelivery.map((generator: Generator) => {
        return <div key={generator.id + deliveryId}>{generator.name}</div>;
      })}
    </>
  );
}

export { ContractDeliverySubComponent };
